<template>
  <div
    :class="[
      'custom-input',
      {
        'custom-input--value': value || hasValue,
        'custom-input--focus': hasFocus,
        'custom-input--stripe': isStripe,
        'is-valid': isValid,
        'is-invalid': isInvalid || hasError
      }
    ]"
  >
    <span @click="autofocus">{{ placeholder }}</span>
    <label class="stripe-placeholder">{{ placeholder }}</label>
    <component
      ref="input"
      :id="id"
      :is="component"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      v-bind:value="value"
      v-bind="$attrs"
      v-on="$listeners"
      @focus="onFocus($event)"
      @blur="onBlur($event)"
    >
    </component>

    <slot></slot>

    <div v-if="hasSlot('link')" class="custom-input__link">
      <slot name="link"></slot>
    </div>
    <div v-if="hasSlot('icon')" class="custom-input__icon">
      <slot name="icon"></slot>
    </div>
  </div>
</template>
<script>
/**
 * ==================================================================================
 * Custom input that still uses `bootstrap VUE's` own textfield & textarea input
 * ==================================================================================
 **/

export default {
  props: {
    value: {
      type: [String, Number]
    },
    component: {
      type: String,
      default: 'b-input',
      validation: function(value) {
        return (
          [
            'b-input',
            'b-form-input',
            'b-textarea',
            'b-form-textarea',
            /* For converting into a Stripe element */
            'div'
          ].indexOf(value) !== -1
        )
      }
    },
    id: {
      type: String
    },
    placeholder: {
      type: String,
      default: 'Placeholder'
    },
    autocomplete: {
      type: String,
      default: 'on'
    },
    isValid: {
      type: [Boolean, String, Array],
      default: false
    },
    isInvalid: {
      type: [Boolean, String, Array],
      default: false
    },
    inputClass: {
      type: [String, Array, Object],
      default: null
    },

    /**
     * Setup component for Stripe integration
     *
     * Changes will be:
     * - Stripe will create its input field INSIDE an iFrame
     * - Create the default placeholder (Due to lack of CSS availability on the for the Stripe element)
     * - Focus trigger would be checking the created Stripe element's parent container for class changes
     *   (Using MutationObserver, https://stripe.com/docs/js/element/the_element_container)
     */
    isStripe: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      hasFocus: false,
      /* Stripe trigger states */
      hasValue: false,
      hasError: false
    }
  },
  methods: {
    focus() {
      this.$refs['input'].focus()
    },

    autofocus() {
      this.$refs['input'].focus()
    },

    onFocus(e) {
      this.hasFocus = true
      this.$emit('focus', e)
    },
    onBlur(e) {
      this.hasFocus = false
      this.$emit('blur', e)
    },

    hasSlot(name = 'default') {
      return !!this.$slots[name] || !!this.$scopedSlots[name]
    },

    /**
     * Stripe!
     * ==================================================================================
     **/

    toStripe() {
      if (!this.isStripe) return

      this.observeClass('StripeElement--focus', state => {
        this.hasFocus = state
      })
      this.observeClass('StripeElement--empty', state => {
        this.hasValue = !state
      })
      this.observeClass('StripeElement--invalid', state => {
        this.hasError = state
      })
    },

    observeClass(cls, callback) {
      let stripeElem = this.$el.getElementsByClassName('StripeElement')[0]
      if (stripeElem) {
        let prevClassState = stripeElem.classList.contains(cls)
        let observer = new MutationObserver(mutations => {
          mutations.forEach(mutation => {
            if (mutation.attributeName === 'class') {
              let currentClassState = mutation.target.classList.contains(cls)
              if (prevClassState !== currentClassState) {
                prevClassState = currentClassState
                callback(currentClassState)
              }
            }
          })
        })

        observer.observe(stripeElem, { attributes: true })
      }
    }
  }
}
</script>
